<template>
    <div>
        <top-menu @menu-item-click="onMenuItemClick" />
        <tab-bar
            id="ai-main-tab"
            :views="openedTabs"
            @tab-item-click="onTabTitleClick"
            @close-tab-item-click="onCloseTabItemClick"
            @close-by-context-menu="onCloseByContextMenu"
        />
    </div>
    <hr id="divider" />
</template>

<script setup>
import { useRouter } from 'vue-router';

import TopMenu from '@/layouts/main-layout/components/top-menu/ai-top-menu.vue';
import TabBar from '@/layouts/main-layout/components/tab-bar/tab-bar.vue';
import AiCustomConfirmDialog from '@/utils/ai-custom-confirm-dialog';
import { getRouter } from '@/utils/meta-route';
import {
    findTabByPath,
    getFocusedTab,
    getHomeView,
    getOpenedTabs,
    loadTabs,
    removeTab,
    saveOpenedTabs,
    setFocusedTab,
    setFuncRefreshOpenTabs,
    tabFocusChange,
    setFuncChangeRoute,
    saveTabContent,
    resetTabState,
    childViewId,
} from '@/layouts/main-layout/components/navigation-bars-data';

import { onBeforeMount, ref } from 'vue';
import { setSaveUserDataPopupVisible } from '../main-layout-data';
import { processTab } from './top-menu/utils';
import { deleteUserDataChanges } from '@/service/userdata-service';

onBeforeMount(async () => {
    await loadTabs();
    openedTabs.value = getOpenedTabs();
    setFuncRefreshOpenTabs(refreshOpenTabs);
    setFuncChangeRoute(changeRoute);
});

const router = useRouter();

const openedTabs = ref([]);

const onTabTitleClick = view => {
    changeRoute(view);
};

const onCloseTabItemClick = async view => {
    const focusedTab = getFocusedTab();
    if (view.routePath === focusedTab.routePath) {
        await router.push({ name: 'home-view' });
        const homeView = getHomeView();
        setFocusedTab(homeView);
    }
    await removeTab(view);
    await saveOpenedTabs();
    openedTabs.value = getOpenedTabs();
};

const onCloseByContextMenu = async itemData => {
    const uiRoute = window.location.pathname;
    const allViews = getOpenedTabs();
    const dirtyTabs = allViews.filter(tab => tab.isDirty);
    const focusedTabPath = getFocusedTab().routePath;

    const viewId = childViewId || dirtyTabs.find(tab => tab.viewId === itemData.viewId)?.viewId;

    const handleClose = async (shouldSave, tabsToClose) => {
        const dirtyTabsToClose = tabsToClose.filter(tab => tab.isDirty);
        const originalTabsToClose = [...tabsToClose];

        const closeClearTabs = async () => {
            for (const view of originalTabsToClose) {
                if (view.viewId !== 'home-view' && !view.isDirty) {
                    removeTab(view);
                    await saveOpenedTabs();
                }
            }
        };

        const goToHomeTab = async () => {
            await router.push({ name: 'home-view' });
            const homeView = getHomeView();
            setFocusedTab(homeView);
            await router.push({ name: 'home-view' });
        };

        if (shouldSave && dirtyTabsToClose.length) {
            setSaveUserDataPopupVisible(true);
            const success = await processTab(dirtyTabsToClose[0], 0, dirtyTabsToClose, saveTabContent, shouldSave);
            setSaveUserDataPopupVisible(false);
            if (success) {
                deleteUserDataChanges({ viewId, uiRoute });
                resetTabState();
            }

            await closeClearTabs();
        }

        if (!shouldSave || !dirtyTabsToClose.length) {
            for (const view of originalTabsToClose) {
                if (view.viewId !== 'home-view' && !view.isDirty) {
                    const focusedTab = getFocusedTab();
                    if (view.routePath === focusedTab.routePath) {
                        await goToHomeTab();
                    }
                    removeTab(view);
                    await saveOpenedTabs();
                    openedTabs.value = getOpenedTabs();
                }
            }
        }

        if (!shouldSave && dirtyTabsToClose.length) {
            setSaveUserDataPopupVisible(true);
            await processTab(dirtyTabsToClose[0], 0, dirtyTabsToClose, saveTabContent, shouldSave);
            setSaveUserDataPopupVisible(false);
            resetTabState();

            await closeClearTabs();
            await goToHomeTab();
        }
    };

    if (itemData.name === 'closeAll') {
        let result;
        if (dirtyTabs.length) {
            result = await AiCustomConfirmDialog(
                'В закрывающихся вкладках есть несохраненные изменения. Сохранить изменения?'
            );
            await handleClose(result === 1, allViews);
        } else {
            handleClose(false, allViews);
        }
    } else {
        const tabsToClose = allViews.filter(view => view.routePath !== focusedTabPath && view.viewId !== 'home-view');
        const dirtyTabsToClose = tabsToClose.filter(tab => tab.isDirty);

        let result;
        if (dirtyTabsToClose.length) {
            result = await AiCustomConfirmDialog(
                'В закрывающихся вкладках есть несохраненные изменения. Сохранить изменения?'
            );
            await handleClose(result === 1, tabsToClose);
        } else {
            handleClose(false, tabsToClose);
        }
    }
};

const onMenuItemClick = async name => {
    // changeRoute({ routeName: name });
    await router.replace({ name });
};

const changeRoute = async view => {
    if (view === undefined) {
        return;
    }
    await router.push({ name: view.routeName, params: view.params });
    const tab = findTabByPath(view.routePath);

    if (!tab) {
        const newRout = await getRouter(view);
        if (newRout.result === true) {
            router.addRoute(newRout.data);
        }
    }

    await tabFocusChange(view);
    openedTabs.value = await getOpenedTabs();
};

async function refreshOpenTabs() {
    openedTabs.value = await getOpenedTabs();
}
</script>

<style scoped>
#divider {
    margin: 5px 10px;
}
</style>
